import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer, MatDrawerContent } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, fromEvent, merge, of } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { filter, map, startWith, take, takeUntil } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api-services/api.service';
import { AxiosService } from 'src/app/services/axios.service';
import { SharedService } from 'src/app/services/shared.service';
import { PasswordChangeModalComponent } from 'src/app/settings/password-change-modal/password-change-modal.component';
import {
  selectAuth,
  selectOrgSettings,
} from 'src/app/store/auth/auth.selector';
import {
  initialTokenAuthState,
  TokenAuthState,
} from 'src/app/store/auth/auth.state';
import {
  getTokenFromStorage,
  saveTokenToStorage,
  showError,
} from 'src/app/utility/common-logic';
import { environment } from 'src/environments/environment';
import Breadcrumb from '../../utility/model/breadcrumb';
import { ConsentBoxComponent } from '../consent-box/consent-box.component';
import { AuthService } from 'src/app/store/auth/auth.service';
import { AutoUnsubscribeComponent } from 'src/app/utility/auto-unsubscribe.component';
import { ShowRedemptionInfoDialogComponent } from '../show-redemption-info-dialog/show-redemption-info-dialog.component';
import { EventBroadcastService } from 'src/app/services/event-broadcast.service';
import { BroadcastEvents } from 'src/app/utility/model/broadcast-payload';
import { detectDevice } from 'src/app/store/device/device.actions';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent
  extends AutoUnsubscribeComponent
  implements OnInit, OnDestroy
{
  auth$: TokenAuthState;
  hasBackdrop = true;
  mode: 'over' | 'push' | 'side' = 'over';
  @Input() hideHeader = '';
  @Input() hideFooter = environment.settings.showFooter === '1' ? '' : '1';
  @Input() stickyFooter = '';
  @ViewChild('leftDrawer') leftDrawer: MatDrawer | undefined;
  @ViewChild('rightDrawer') rightDrawer: MatDrawer | undefined;
  @ViewChild('drawerContent') drawerContent!: MatDrawerContent;
  @Output() drawerClosed: EventEmitter<void> = new EventEmitter();
  @Input() breadcrumb: Breadcrumb[] = [];
  @Input() layoutBgColor = '';
  @Input() homePage = false;
  @Input() loadApi = true;
  showScrollButton = false;
  hideLeftDrawer = false;
  hideRightDrawer = false;
  rightDrawerIndex = 0;
  networkStatus = false;
  networkStatus$: Subscription = Subscription.EMPTY;
  applyBgColorFromDjango = environment.settings.applyBgColorFromDjango;
  loaded = false;
  sessionTime: number;
  showBottomMenu = environment.settings.showBottomMenu;
  appName = environment.settings?.appName;
  leftNavigate = environment.settings.leftNavigate;
  showServiceBanner = environment.settings.showServiceBanner;
  showSessionTimer = environment.settings.showSessionTimer;
  showSessionTimerNote = environment.settings.showSessionTimerNote;
  freshchat = environment.settings.freshchat;
  showFloatingBtnFromLayout = environment.settings.showFloatingBtnFromLayout;
  hasAcceptedTermsCondition = Boolean(
    localStorage.getItem('has_accepted_terms_and_cond') ?? false,
  );
  app = environment.settings.app;
  supportChat = environment.settings.supportChat;
  timerStarted = false;
  userSuccess$: any;
  userData: any;
  tokenAuth = initialTokenAuthState.auth;
  showPointDetail = environment.settings.header.pointDetail;
  showHeader = environment.settings.header.showHeader;
  showFooter = environment.settings.footer.showFooter;

  constructor(
    private router: Router,
    public translate: TranslateService,
    public dialog: MatDialog,
    private toastr: ToastrService,
    private api: ApiService,
    private shared: SharedService,
    private axiosService: AxiosService,
    private store: Store,
    private authService: AuthService,
    private eventService: EventBroadcastService,
  ) {
    super();
    this.loaded = true;
    this.shared.spinner = true;
  }

  getData(auth: any) {
    saveTokenToStorage(auth?.auth?.token);
  }

  get isLoginTnc() {
    return this.router.url === '/login-tnc';
  }

  async ngOnInit() {
    this.store.pipe(select(selectAuth)).subscribe((auth) => {
      this.auth$ = auth;
      this.tokenAuth = auth?.auth;
      if (auth?.auth?.token) {
        this.getData(auth);
        this.loaded = false;
      }
    });
    this.shared.spinner = false;

    const orgSettings$ = this.store.pipe(select(selectOrgSettings));

    const navigationEnd$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
    );

    combineLatest([navigationEnd$, orgSettings$])
      .pipe(
        takeUntil(this.destroy$),
        filter(([_, settings]) => !!settings),
      )
      .subscribe(([_, settings]) => {
        if (!!settings?.preferred_home_page && this.app === 'basf') {
          this.openShowRedemptionInfoDialog(settings?.preferred_home_page);
        }
      });

    if (
      this.auth$.organization_details?.ask_to_accept_terms_and_conditions >=
        1 &&
      !this.hasAcceptedTermsCondition &&
      !this.auth$.user_details?.has_accepted_terms_and_cond
    ) {
      this.openConsentBox();
    }

    if (
      !this.isLoginTnc &&
      (localStorage.length === 0 || !getTokenFromStorage())
    ) {
      await this.router.navigate(['/login']);
    }
    this.store.dispatch(detectDevice());
    this.axiosService.configureAxios();
    this.checkNetworkStatus();
    if (this.auth$?.organization_settings?.enable_password_change == 1) {
      this.checkPassword();
    }
  }

  openShowRedemptionInfoDialog(data: string | null) {
    const dialogRef = this.dialog.open(ShowRedemptionInfoDialogComponent, {
      data: data,
      disableClose: true,
      maxWidth: '90vw',
      maxHeight: '90vh',
    });
  }

  override ngOnDestroy() {
    this.networkStatus$.unsubscribe();
  }

  openSupportChat() {
    window.open(this.supportChat.link, 'blank');
  }

  // To check internet connection stability
  checkNetworkStatus() {
    this.networkStatus = navigator.onLine;
    this.networkStatus$ = merge(
      of(null),
      fromEvent(window, 'online'),
      fromEvent(window, 'offline'),
    )
      .pipe(map(() => navigator.onLine))
      .subscribe((status) => {
        this.networkStatus = status;
        if (!status) {
          showError(
            'msg.internet.lost',
            this.toastr,
            this.translate,
            true,
            true,
          );
        }
      });
  }

  checkPassword() {
    if (this.auth$?.user_details?.using_default_password) {
      this.dialog.open(PasswordChangeModalComponent, {
        disableClose: true,
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: { isModal: true },
      });
    }
  }

  toggleDrawer(direction: 'left' | 'right') {
    if (direction === 'left') {
      this.hideLeftDrawer = false;
      this.leftDrawer?.toggle();
    }
    if (direction === 'right') {
      this.hideRightDrawer = false;
      this.rightDrawer?.toggle();
    }
  }

  openConsentBox() {
    const dialogRef = this.dialog.open(ConsentBoxComponent, {
      disableClose: true,
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: { isModal: true },
    });
  }

  onDrawerClosed(): void {
    this.drawerClosed.emit();
  }
}
